import React from "react";
import { useTheme } from "../contexts/ThemeContext";
import { useLang } from "../contexts/LanguageContext";
import AppLayout from "../components/AppLayout";
import BottomNav from "../components/BottomNav";
import { ArrowLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

const ARTICLES = {
  "signification-reve-serpent": {
    title: "Signification du Rêve de Serpent en Islam",
    titleAr: "تفسير حلم الثعبان في الإسلام",
    content: `Le serpent est l'un des symboles les plus fréquents et les plus puissants dans les rêves. Dans la tradition islamique de l'interprétation des rêves (ta'bir al-ru'ya), le serpent peut avoir de nombreuses significations selon le contexte du rêve et la situation personnelle du rêveur.

## Le serpent dans la tradition islamique

Selon Ibn Sirin, l'un des plus grands interprètes de rêves de l'histoire islamique, le serpent dans un rêve peut représenter un ennemi caché ou une personne malveillante dans l'entourage du rêveur. Cependant, cette interprétation n'est pas universelle et dépend de nombreux facteurs.

Dans le Coran, le serpent apparaît dans l'histoire du prophète Moïse (Moussa, paix sur lui), dont le bâton se transformait en serpent par la volonté d'Allah. Ce récit montre que le serpent peut aussi symboliser le pouvoir divin et la transformation spirituelle.

## Les différentes significations selon le contexte

**Voir un serpent sans être mordu** : Cela peut indiquer la présence d'un ennemi qui ne peut pas vous nuire. C'est souvent interprété comme un signe de protection divine.

**Être mordu par un serpent** : Dans la tradition islamique, cela peut symboliser des paroles blessantes ou une trahison de la part d'un proche. Il est recommandé de faire des invocations (du'a) et de chercher refuge auprès d'Allah.

**Tuer un serpent** : C'est généralement considéré comme un signe positif, indiquant la victoire sur un ennemi ou le dépassement d'une épreuve. Ibn Sirin rapporte que tuer un serpent en rêve est un présage favorable.

**Un serpent qui entre dans la maison** : Cela peut représenter un visiteur indésirable ou une situation problématique qui entre dans votre vie familiale.

**Un serpent de couleur verte** : Dans l'Islam, le vert est une couleur bénie. Un serpent vert peut paradoxalement représenter une bénédiction déguisée ou une épreuve qui mènera à un bien.

## L'approche psychologique complémentaire

Du point de vue de la psychologie moderne, le serpent dans les rêves est souvent associé à la transformation, au renouveau (car le serpent mue), ou à des peurs profondes. Carl Jung considérait le serpent comme un symbole de l'inconscient et de la sagesse cachée.

## Que faire après un rêve de serpent ?

La tradition islamique recommande plusieurs choses après un mauvais rêve :
1. Chercher refuge auprès d'Allah en disant "A'udhu billahi min ash-shaytani ar-rajim"
2. Cracher légèrement trois fois à gauche
3. Changer de position de sommeil
4. Ne pas raconter le rêve aux autres si on pense qu'il est mauvais
5. Faire la prière et les invocations

DreamNoor combine cette sagesse traditionnelle avec l'intelligence artificielle pour vous offrir une interprétation personnalisée de vos rêves, tenant compte de votre situation personnelle et de votre état émotionnel.`,
  },
  "signification-reve-mort": {
    title: "Signification du Rêve de Mort en Islam",
    titleAr: "تفسير حلم الموت في الإسلام",
    content: `Rêver de la mort est une expérience troublante qui touche presque tout le monde au cours de sa vie. Dans l'Islam, ce type de rêve possède des significations profondes qui vont bien au-delà de la peur naturelle qu'il peut susciter.

## La mort dans les rêves selon la tradition islamique

Contrairement à ce que beaucoup pensent, rêver de la mort n'annonce généralement pas un décès réel. Selon les grands interprètes islamiques comme Ibn Sirin et An-Nabulsi, la mort en rêve symbolise souvent un changement majeur, une transformation ou un renouveau dans la vie du rêveur.

Le Prophète Muhammad (paix et bénédictions sur lui) a enseigné que les rêves véridiques font partie des quarante-six parties de la prophétie. Cependant, tous les rêves ne sont pas des messages divins — certains proviennent de l'âme (nafs) ou du shaytan.

## Les différentes interprétations

**Rêver de sa propre mort** : Paradoxalement, cela est souvent interprété comme un signe de longévité. Ibn Sirin explique que voir sa propre mort en rêve peut signifier un repentir sincère, un retour à la foi, ou la fin d'une période difficile suivie d'un renouveau.

**Rêver de la mort d'un parent** : Cela peut symboliser des changements dans la relation avec ce parent, ou refléter une inquiétude profonde pour cette personne. C'est rarement un présage de mort réelle.

**Rêver de la mort d'un inconnu** : An-Nabulsi interprète cela comme la fin d'une situation ou d'un comportement dans la vie du rêveur.

**Voir un défunt vivant en rêve** : C'est souvent considéré comme un rêve véridique. La personne défunte peut transmettre un message, demander des prières (du'a) ou des aumônes (sadaqah) en son nom.

**Rêver d'un enterrement** : Cela peut symboliser la fin d'un chapitre de vie et le début d'un nouveau. L'enterrement représente le passage, la transition.

## La sagesse islamique face à la mort

L'Islam enseigne que la mort fait partie du plan divin. Le Coran dit : "Toute âme goûtera la mort" (Sourate Al-Imran, 3:185). Cette acceptation permet d'aborder les rêves de mort avec sérénité plutôt qu'avec terreur.

## L'approche de DreamNoor

Notre intelligence artificielle analyse votre rêve de mort en tenant compte de votre contexte personnel — votre âge, votre situation familiale, votre état émotionnel — pour vous offrir une interprétation personnalisée qui combine la richesse de la tradition islamique et les éclairages de la psychologie moderne.

N'oubliez pas : un rêve de mort est presque toujours un symbole de transformation, pas une prédiction. C'est un appel à la réflexion et au renouveau spirituel.`,
  },
  "signification-reve-chat": {
    title: "Signification du Rêve de Chat en Islam",
    titleAr: "تفسير حلم القطة في الإسلام",
    content: `Le chat occupe une place particulière dans la culture islamique. Le Prophète Muhammad (paix et bénédictions sur lui) était connu pour son amour des chats. Cette relation spéciale influence naturellement l'interprétation des rêves impliquant des chats.

## Le chat dans la tradition islamique

Le Prophète Muhammad (paix et bénédictions sur lui) avait un chat nommé Muezza qu'il chérissait. Il est rapporté qu'il préférait couper la manche de son vêtement plutôt que de déranger son chat endormi dessus. Cette tendresse prophétique envers les chats donne à ces animaux une symbolique positive dans l'Islam.

Selon Ibn Sirin, le chat en rêve peut représenter un serviteur, un gardien, ou une personne de l'entourage du rêveur. L'interprétation varie selon la couleur du chat, son comportement et les émotions ressenties pendant le rêve.

## Les différentes significations

**Un chat amical et doux** : Représente souvent la douceur, la protection et la bénédiction dans le foyer. C'est un signe positif qui peut indiquer la présence de personnes bienveillantes autour de vous.

**Un chat agressif ou qui griffe** : Peut symboliser une trahison ou une personne hypocrite dans votre entourage. An-Nabulsi interprète le chat agressif comme un avertissement de se méfier de certaines personnes.

**Un chat noir** : Contrairement aux superstitions occidentales, le chat noir en rêve dans la tradition islamique peut simplement représenter un djinn ou une énergie négative. Il est recommandé de chercher refuge auprès d'Allah.

**Un chat blanc** : Généralement interprété de manière positive, symbolisant la pureté, la paix intérieure et la bénédiction.

**Nourrir un chat** : Signe de générosité et de bonne action. Cela peut indiquer que vos actes de charité sont acceptés.

**Un chat qui miaule** : Peut représenter des commérages ou des paroles prononcées à votre sujet. C'est un rappel de prêter attention à votre entourage.

## La symbolique psychologique

En psychologie, le chat symbolise souvent l'indépendance, l'intuition féminine et le mystère. Jung voyait dans le chat un symbole de l'anima — la part féminine de la psyché.

## L'interprétation personnalisée avec DreamNoor

Chaque rêve est unique car il est lié à l'histoire personnelle du rêveur. DreamNoor utilise l'intelligence artificielle pour analyser votre rêve de chat en tenant compte de vos émotions, votre situation et votre contexte spirituel, vous offrant ainsi une interprétation riche et personnalisée.`,
  },
  "signification-reve-eau": {
    title: "Signification du Rêve d'Eau en Islam",
    titleAr: "تفسير حلم الماء في الإسلام",
    content: `L'eau est l'un des éléments les plus symboliques dans les rêves et dans l'Islam. Le Coran mentionne l'eau plus de 60 fois, soulignant son importance fondamentale. Allah dit : "Nous avons fait de l'eau toute chose vivante" (Sourate Al-Anbiya, 21:30).

## L'eau dans le Coran et la tradition islamique

L'eau dans l'Islam représente la vie, la purification et la miséricorde divine. Les ablutions (wudu) avant la prière utilisent l'eau comme moyen de purification physique et spirituelle. Cette dimension purificatrice se retrouve naturellement dans l'interprétation des rêves.

Ibn Sirin considère l'eau comme l'un des symboles les plus riches dans les rêves. Sa signification dépend de sa clarté, sa quantité, son mouvement et le contexte dans lequel elle apparaît.

## Les différentes significations selon le type d'eau

**Eau claire et pure** : C'est l'un des meilleurs signes en rêve. Elle représente la foi pure, la connaissance, la guérison et la bénédiction divine. Boire de l'eau claire en rêve est un signe de bonne santé spirituelle.

**Eau trouble ou sale** : Peut symboliser des doutes, des épreuves ou une période de confusion spirituelle. C'est un appel à la purification intérieure et au retour vers Allah.

**Eau qui coule (rivière)** : Représente le flux de la vie, la subsistance (rizq) et les bénédictions continues. Une rivière calme indique la paix intérieure.

**Mer ou océan** : Symbolise souvent un dirigeant puissant, une grande connaissance ou les épreuves de la vie. La mer calme est un signe de tranquillité, tandis que la mer agitée représente les turbulences émotionnelles.

**Pluie** : Dans la tradition islamique, la pluie est une miséricorde d'Allah. Rêver de pluie est généralement un signe de bénédiction, de prospérité et de renouveau. Le Coran dit : "C'est Lui qui envoie la pluie après que les gens ont désespéré" (Sourate Ash-Shura, 42:28).

**Inondation** : Peut représenter des épreuves importantes mais aussi une purification collective. L'histoire du prophète Noé (Nuh, paix sur lui) montre que l'inondation peut être un instrument de renouveau divin.

**Se noyer** : Symbolise souvent le sentiment d'être submergé par les difficultés. C'est un appel à chercher l'aide d'Allah et à renforcer sa foi.

## L'eau et la purification spirituelle

L'eau dans les rêves islamiques est intimement liée à la notion de tazkiyah (purification de l'âme). Un rêve d'eau peut être une invitation divine à purifier son cœur, à renouveler sa foi et à se rapprocher d'Allah.

## DreamNoor et l'interprétation des rêves d'eau

Notre application utilise l'intelligence artificielle pour analyser les multiples dimensions de votre rêve d'eau — le type d'eau, votre interaction avec elle, vos émotions — et vous offre une interprétation qui combine la sagesse d'Ibn Sirin avec la psychologie moderne, le tout personnalisé selon votre situation.`,
  },
  "signification-reve-vol": {
    title: "Signification du Rêve de Vol (Voler dans les airs) en Islam",
    titleAr: "تفسير حلم الطيران في الإسلام",
    content: `Voler dans un rêve est l'une des expériences oniriques les plus extraordinaires et les plus fréquentes. Presque tout le monde a déjà rêvé de voler au moins une fois dans sa vie. Dans la tradition islamique, le vol en rêve possède des significations spirituelles profondes.

## Le vol dans les rêves selon la tradition islamique

Ibn Sirin interprète le vol en rêve comme un signe d'élévation spirituelle, de voyage ou d'ambition. La capacité de voler dans un rêve reflète souvent le désir de l'âme de se libérer des contraintes terrestres et de se rapprocher d'Allah.

An-Nabulsi ajoute que voler en rêve peut représenter l'obtention d'un rang élevé, que ce soit dans la société, dans la connaissance ou dans la spiritualité.

## Les différentes significations selon le contexte

**Voler haut dans le ciel** : Symbolise une grande ambition, une élévation spirituelle et la proximité avec Allah. C'est un rêve très positif qui peut annoncer un succès important ou un avancement spirituel.

**Voler bas, près du sol** : Peut représenter un voyage court ou un changement mineur dans la vie. Si le vol est difficile, cela peut indiquer des obstacles à surmonter.

**Voler avec des ailes** : Les ailes en rêve islamique symbolisent le pouvoir et la capacité donnés par Allah. Voler avec des ailes peut représenter la foi qui permet de surmonter les épreuves.

**Voler sans ailes** : Peut symboliser des souhaits qui se réaliseront par la grâce d'Allah, ou une transformation spirituelle en cours.

**Voler au-dessus de la Kaaba ou d'une mosquée** : C'est un rêve particulièrement béni qui peut symboliser un pèlerinage à venir (Hajj ou Umra) ou une grande élévation spirituelle.

**Tomber en plein vol** : Représente souvent la peur de l'échec ou une mise en garde contre l'orgueil (kibr). L'Islam enseigne que l'élévation véritable vient de l'humilité devant Allah.

**Voler la nuit** : La nuit dans l'Islam est un moment de spiritualité intense (pensez à la prière de la nuit, tahajjud). Voler la nuit peut symboliser un voyage spirituel profond.

## Le voyage nocturne du Prophète (Isra et Mi'raj)

Le vol en rêve rappelle le voyage nocturne miraculeux du Prophète Muhammad (paix et bénédictions sur lui) — l'Isra et Mi'raj — où il fut transporté de La Mecque à Jérusalem puis aux cieux. Ce voyage divin est l'exemple suprême de l'élévation spirituelle dans l'Islam.

## L'interprétation psychologique

La psychologie moderne considère le rêve de vol comme un symbole de liberté, de confiance en soi et de dépassement de ses limites. Freud y voyait un désir de libération, tandis que Jung l'interprétait comme l'expression de la transcendance de l'ego.

## Personnalisez votre interprétation avec DreamNoor

Chaque rêve de vol est unique. L'altitude, la direction, les émotions ressenties — tout compte dans l'interprétation. DreamNoor analyse tous ces éléments grâce à l'intelligence artificielle et vous offre une interprétation sur mesure, enracinée dans la tradition islamique et éclairée par la compréhension moderne de la psyché humaine.`,
  },
  "signification-reve-mariage": {
    title: "Signification du Rêve de Mariage en Islam",
    titleAr: "تفسير حلم الزواج في الإسلام",
    content: `Le mariage est l'un des rêves les plus courants et les plus significatifs dans la tradition islamique. Le Coran décrit le mariage comme un signe d'Allah : "Et parmi Ses signes, Il a créé de vous, pour vous, des épouses pour que vous viviez en tranquillité avec elles" (Sourate Ar-Rum, 30:21).

## Le mariage dans les rêves selon Ibn Sirin

Ibn Sirin considère le rêve de mariage comme un symbole riche dont la signification varie selon les détails. En règle générale, le mariage en rêve représente un engagement important, une nouvelle phase de vie, ou une responsabilité à venir.

**Rêver de son propre mariage (si célibataire)** : C'est souvent un signe positif annonçant un mariage réel à venir, un nouveau projet, ou une bénédiction imminente. An-Nabulsi ajoute que cela peut symboliser l'obtention d'une faveur divine.

**Rêver de son propre mariage (si déjà marié)** : Peut représenter un renouveau dans la relation conjugale, un nouveau départ, ou une responsabilité supplémentaire comme un nouvel emploi ou un enfant à venir.

**Assister au mariage de quelqu'un d'autre** : Symbolise souvent la joie partagée, la bénédiction communautaire, ou un événement heureux dans l'entourage.

**Un mariage triste ou problématique** : Peut refléter des inquiétudes concernant un engagement ou une décision importante. C'est un appel à la réflexion et à l'istikharah (prière de consultation).

## La dimension spirituelle

Dans l'Islam, le mariage est considéré comme la moitié de la foi. Le Prophète Muhammad ﷺ a dit : "Quand un serviteur se marie, il a complété la moitié de sa religion." Rêver de mariage peut donc symboliser un rapprochement spirituel avec Allah.

## L'interprétation avec DreamNoor

Notre intelligence artificielle prend en compte votre situation personnelle — êtes-vous célibataire, marié(e), en recherche — pour personnaliser l'interprétation de votre rêve de mariage selon la tradition islamique et la psychologie moderne.`,
  },
  "signification-reve-bebe": {
    title: "Signification du Rêve de Bébé en Islam",
    titleAr: "تفسير حلم الطفل الرضيع في الإسلام",
    content: `Rêver d'un bébé est une expérience onirique fréquente qui porte des significations profondes dans la tradition islamique. Le bébé symbolise la pureté, le renouveau et la bénédiction divine.

## Le bébé dans les rêves selon la tradition islamique

Ibn Sirin interprète le bébé dans les rêves comme un symbole de bonne nouvelle, de projet naissant ou de bénédiction à venir. Le Coran mentionne plusieurs naissances miraculeuses — celle de Jésus (Issa), de Jean-Baptiste (Yahya) et d'Isaac (Ishaq) — soulignant que chaque enfant est un don d'Allah.

**Voir un beau bébé souriant** : C'est l'un des meilleurs signes en rêve. Il représente la joie, la bénédiction, une bonne nouvelle imminente, ou un nouveau départ béni.

**Porter un bébé dans ses bras** : Symbolise une responsabilité nouvelle qui apportera de la joie. Cela peut annoncer un projet réussi, une grossesse, ou une promotion.

**Un bébé qui pleure** : Peut représenter une inquiétude ou un besoin non satisfait dans votre vie. C'est un appel à prêter attention à quelque chose que vous négligez.

**Allaiter un bébé** : Pour une femme, c'est un signe de générosité et de nourriture spirituelle. An-Nabulsi l'interprète comme un signe de subsistance (rizq) abondante.

**Un bébé malade** : Peut symboliser un projet en difficulté ou une inquiétude concernant un proche. C'est un rappel de faire des du'a et de chercher l'aide d'Allah.

## La symbolique psychologique

En psychologie, le bébé dans les rêves représente notre "enfant intérieur", nos projets naissants, notre vulnérabilité ou notre besoin de protection. Jung voyait dans le bébé un symbole du "Soi" en devenir.

## L'approche personnalisée de DreamNoor

DreamNoor analyse votre rêve de bébé en tenant compte de votre situation — votre âge, votre désir de parentalité, vos projets en cours — pour offrir une interprétation qui résonne avec votre vécu personnel.`,
  },
  "signification-reve-mer": {
    title: "Signification du Rêve de la Mer en Islam",
    titleAr: "تفسير حلم البحر في الإسلام",
    content: `La mer est l'un des symboles les plus puissants et les plus riches dans l'interprétation des rêves islamiques. Sa vastitude, sa profondeur et sa force en font un miroir des émotions humaines et de la puissance divine.

## La mer dans le Coran

La mer est mentionnée de nombreuses fois dans le Coran. Allah dit : "C'est Lui qui a assujetti la mer pour que vous en mangiez une chair fraîche" (Sourate An-Nahl, 16:14). La mer représente à la fois les bienfaits d'Allah et Sa puissance infinie.

L'histoire du prophète Moïse (Moussa, paix sur lui) traversant la mer Rouge est l'un des récits les plus marquants du Coran, symbolisant la délivrance divine et le passage d'un état à un autre.

## Les interprétations selon Ibn Sirin

**La mer calme et bleue** : Représente la paix intérieure, la sérénité spirituelle et la proximité avec Allah. C'est un très bon signe qui indique une période de tranquillité.

**La mer agitée ou tempétueuse** : Symbolise les épreuves de la vie, les turbulences émotionnelles ou les défis à venir. An-Nabulsi l'interprète comme un avertissement de se préparer spirituellement.

**Nager dans la mer** : Si vous nagez avec aisance, cela représente votre capacité à traverser les épreuves avec la grâce d'Allah. Si vous luttez, c'est un appel à renforcer votre foi.

**Boire l'eau de mer** : Peut symboliser la connaissance ou le pouvoir, mais aussi l'amertume si l'eau est salée. Ibn Sirin y voit parfois un signe de richesse acquise avec difficulté.

**Marcher sur la mer** : Un rêve rare et puissant qui symbolise une foi exceptionnelle ou un miracle à venir. C'est un signe de grande élévation spirituelle.

**La mer qui déborde** : Peut représenter des émotions submergantes ou un changement majeur dans votre vie. C'est un rappel de chercher refuge auprès d'Allah.

## DreamNoor : votre guide d'interprétation

Notre application analyse chaque détail de votre rêve de mer — la couleur de l'eau, votre position, vos émotions — pour vous offrir une interprétation complète qui combine la richesse de la tradition islamique avec les éclairages de la psychologie moderne.`,
  },
};

export default function ArticlePage() {
  const { slug } = useParams();
  const { theme } = useTheme();
  const { lang } = useLang();
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const textPrimary = isDark ? "text-white" : "text-slate-900";
  const textBody = isDark ? "text-slate-300" : "text-slate-600";
  const labelColor = isDark ? "text-amber-400" : "text-amber-600";

  const article = ARTICLES[slug];

  if (!article) {
    return (
      <AppLayout>
        <div className="px-5 pt-12 pb-24 min-h-screen flex items-center justify-center">
          <p className={textPrimary}>Article non trouvé</p>
        </div>
        <BottomNav />
      </AppLayout>
    );
  }

  const paragraphs = article.content.split("\n\n");

  return (
    <AppLayout>
      <div className="px-5 pt-12 pb-24 scrollbar-hide overflow-y-auto min-h-screen">
        <button onClick={() => navigate(-1)} className={`mb-4 p-2 rounded-full ${isDark ? "hover:bg-white/10" : "hover:bg-black/5"} transition-colors`}>
          <ArrowLeft size={20} className={textPrimary} />
        </button>
        <h1 className={`font-heading text-2xl font-bold ${textPrimary} mb-2 leading-tight`}>{article.title}</h1>
        <p className={`text-sm ${labelColor} mb-6`}>{article.titleAr}</p>

        <div className="space-y-4">
          {paragraphs.map((p, i) => {
            if (p.startsWith("## ")) {
              return <h2 key={i} className={`text-base font-bold ${labelColor} mt-6 mb-2`}>{p.replace("## ", "")}</h2>;
            }
            if (p.startsWith("**") && p.endsWith("**")) {
              return <p key={i} className={`text-sm font-semibold ${textPrimary}`}>{p.replace(/\*\*/g, "")}</p>;
            }
            if (p.startsWith("**")) {
              const parts = p.split("**");
              return (
                <p key={i} className={`text-sm ${textBody} leading-relaxed`}>
                  {parts.map((part, j) => j % 2 === 1 ? <strong key={j} className={textPrimary}>{part}</strong> : part)}
                </p>
              );
            }
            if (p.startsWith("1.") || p.startsWith("2.")) {
              return <p key={i} className={`text-sm ${textBody} leading-relaxed pl-4`}>{p}</p>;
            }
            return <p key={i} className={`text-sm ${textBody} leading-relaxed`}>{p}</p>;
          })}
        </div>

        <div className="mt-8 glass-card p-5 text-center">
          <p className={`text-sm font-semibold ${textPrimary} mb-2`}>Interprétez votre rêve maintenant</p>
          <p className={`text-xs ${textBody} mb-4`}>DreamNoor combine la sagesse islamique et l'IA pour une interprétation personnalisée</p>
          <button onClick={() => navigate("/interpret")} className="btn-gold py-2.5 px-6 text-sm font-semibold">
            Interpréter mon rêve
          </button>
        </div>

        <div className="flex items-center justify-center gap-2 mt-6 flex-wrap">
          {Object.keys(ARTICLES).filter(s => s !== slug).map(s => (
            <button key={s} onClick={() => navigate(`/article/${s}`)} className={`text-[10px] ${labelColor} underline underline-offset-2`}>
              {ARTICLES[s].title.replace("Signification du Rêve ", "").replace("en Islam", "").replace("(Voler dans les airs) ", "")}
            </button>
          ))}
        </div>
      </div>
      <BottomNav />
    </AppLayout>
  );
}

export { ARTICLES };
